import { createContext, useContext, useRef } from 'react';
import { Toast, ToastMessageOptions } from 'primereact/toast';

export interface ToastContextType {
  showToast: (options: ToastMessageOptions) => void;
  showSuccess: (message: ToastMessageOptions) => void;
  showInfo: (message: ToastMessageOptions) => void;
  showWarn: (message: ToastMessageOptions) => void;
  showError: (message: ToastMessageOptions) => void;
}


export const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider = ({ children }) => {
  const toast = useRef<Toast>(null);

  const showToast = (options: ToastMessageOptions) => {
    toast.current?.show(options);
  };

  const showSuccess = ({ summary = 'Success', detail = '', life = 3000 }: ToastMessageOptions) => {
    showToast({ severity: 'success', summary: summary, detail, life: life });
  };

  const showInfo = ({ summary = "Info", detail = '', life = 3000 }: ToastMessageOptions) => {
    showToast({ severity: 'info', summary: summary, detail: detail, life: life });
  };

  const showWarn = ({ summary = "Warning", detail = '', life = 3000 }: ToastMessageOptions) => {
    showToast({ severity: 'warn', summary: summary, detail: detail, life: life });
  };

  const showError = ({ summary = 'Error', detail = '', life = 3000 }: ToastMessageOptions) => {
    showToast({ severity: 'error', summary: summary, detail: detail, life: life });
  };

  const contextValue: ToastContextType = {
    showToast,
    showSuccess,
    showInfo,
    showWarn,
    showError,
  };

  return (
    <ToastContext.Provider value={contextValue}>
      <Toast ref={toast} />
      {children}
    </ToastContext.Provider>
  );
};

export const useToast = () : ToastContextType => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};
